import { LoadingOutlined } from '@ant-design/icons';
import { Drawer, Tooltip } from 'antd';
import Link from 'next/link';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';
import styled, { css } from 'styled-components';

import { ArrowLeftSvg, QuestionMarkSvg, SettingsSvg } from '../../../assets/icons';
import FilledEyeSvg from '../../../assets/icons/FilledEyeSvg';
import MoreSvg from '../../../assets/icons/MoreSvg';
import PlaySvg from '../../../assets/icons/PlaySvg';
import { PollSvg } from '../../../assets/icons/PollSvg';
import UndoSvg from '../../../assets/icons/UndoSvg';
import theme from '../../../assets/theme';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import {
  isOngoingGameModalVisibleSelector,
  TemplateStateActions,
  useDesignerDataContext,
} from '../../../hooks/useDesignerLogic';
import { useDesignerSaveLogic } from '../../../hooks/useDesignerSaveLogic';
import { useDesignerUIContext } from '../../../hooks/useDesignerUIState';
import useEndGame from '../../../hooks/useEndGame';
import useGames from '../../../hooks/useGames';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { useIsIntercomOpen } from '../../../providers/CustomIntercomStateProvider';
import { TimesyncContext } from '../../../providers/TimesyncProvider/TimesyncProvider';
import { ctrlOrCMD } from '../../../utils/navigator';
import { getTitle } from '../../../utils/templateUtils';
import { zIndexes } from '../../../utils/zIndexes';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import ResumeLessonModal from '../../_atoms/ResumeLessonModal';
import SaveCloud from '../../_atoms/SaveCloud';
import BottomDrawerDragHandle from '../../_molecules/BottomDrawerDragHandle';
import CreatorShareComponent from '../../_molecules/CreatorShareComponent';
import SettingsModalDesigner from '../../_molecules/SettingsModalDesigner';
import { DesignerTopBarContainer } from '../../styled/CreatorTopBar';
import VersionControl from './VersionControl';

const DesignerTopBar = ({
  tenantId,
  templateId,
}: {
  tenantId: string;
  templateId: string;
}) => {
  const handleShowSettingsModal = useDesignerUIContext(c => c.handleShowSettingsModal);
  const handleShowSettingsModalCancel = useDesignerUIContext(
    c => c.handleShowSettingsModalCancel,
  );
  const isOnGoingGameModalVisible = useDesignerDataContext(
    isOngoingGameModalVisibleSelector,
  );
  const isUndoDisabled = useDesignerDataContext(c => !c.state.canUndo);
  const isRedoDisabled = useDesignerDataContext(c => !c.state.canRedo);
  const presentationTitle = useDesignerDataContext(
    c => c.state.localTemplate?.title || undefined,
  );
  const localTemplate = useDesignerDataContext(c => c.state.localTemplate);

  const { now } = useContext(TimesyncContext);

  const isGeneratedByAI = useDesignerDataContext(
    c => !!c.state.localTemplate?.metadata?.['aiTopic'],
  );
  const [showVersionControl, setShowVersionControl] = useState(false);

  const handleExit = useDesignerDataContext(context => context.handleExit);
  const handleUndo = useDesignerDataContext(context => context.handleUndo);
  const handleRedo = useDesignerDataContext(context => context.handleRedo);
  const handleExportAsPdf = useDesignerDataContext(context => context.handleExportAsPdf);
  const localTemplateDispatch = useDesignerDataContext(context => context.dispatch);

  const { router } = useRouterMatch();

  const { t } = useTranslation('Host');

  const { isOpen } = useIsIntercomOpen();
  const { show, hide } = useIntercom();

  const isLargerThan500 = useBreakPoint(500);
  const isLargerThan600 = useBreakPoint(600);
  const isLargerThan700 = useBreakPoint(700);
  const isLargerThan850 = useBreakPoint(850);

  const showSettingsModal = useDesignerUIContext(
    context => context.state.showSettingsModal,
  );
  const [shouldShowDownloadAsPDF, setShouldShowDownloadAsPDF] = useState(false);
  const [showOptionsDrawer, setShowOptionsDrawer] = useState(false);
  const [selectedDrawerScreen, setSelectedDrawerScreen] = useState<'default' | 'share'>(
    'default',
  );
  const {
    handlePresent,
    isCreatingGame,
    handlePresentFromBeginning,
    isSavingTemplate,
    isSavingTemplateError,
    isSavingTemplateIdle,
    isSavingTemplateSuccess,
    handleOpenPreview,
    isLoadingPreview,
    autoSaveTemplate,
    isPresentingDisabled,
  } = useDesignerSaveLogic(localTemplate);

  const { end } = useEndGame();

  const { games } = useGames(localTemplate?.id, 1);
  const hasGames = games && games?.length > 0;

  const handleEndLesson = useCallback(() => {
    const activeGame = localTemplate?.content.activeGame;

    if (activeGame?.id && localTemplate) {
      localTemplateDispatch({ type: TemplateStateActions.endGame });
      autoSaveTemplate({
        templateConfig: {
          activeGame: null,
          modifiedAt: new Date().getTime(),
          id: localTemplate.id,
        },
      });
      end({ gameId: activeGame.id });
    }
  }, [autoSaveTemplate, end, localTemplate, localTemplateDispatch]);

  const handleCloseResumeModal = useCallback(() => {
    router.push(`/${tenantId}/home`);
  }, [router, tenantId]);

  const closeDrawer = () => {
    setShowOptionsDrawer(false);
  };

  const handleHelpClicked = () => {
    if (isOpen) {
      hide();
    } else {
      show();
    }
    closeDrawer();
  };

  const handleStartNewLesson = useCallback(() => {
    const activeGame = localTemplate?.content.activeGame;

    if (activeGame?.id && localTemplate) {
      //First we just present a new lesson with the same template but from the start.
      // This will clear the active game in the lesson cache and redirect
      handlePresentFromBeginning(
        {
          ...localTemplate,
          content: { ...localTemplate.content, activeGame: null },
        },
        gameId => {
          // Must set the new active game when this is done. In react query cache.
          autoSaveTemplate({
            templateConfig: {
              activeGame: {
                id: gameId,
                isOngoing: false,
              },
              modifiedAt: now(),
              id: localTemplate.id,
            },
          });
        },
      );

      // In parallell we end the active game in the backend. Which will also clear the previous active
      // game cache.
      end({ gameId: activeGame.id });
    }
  }, [end, handlePresentFromBeginning, localTemplate, autoSaveTemplate, now]);

  return (
    <TopbarContainer>
      <ResumeLessonModal
        onCancel={handleCloseResumeModal}
        isOnGoingGameModalVisible={isOnGoingGameModalVisible}
        onStartNewLesson={handleStartNewLesson}
        onResumeLesson={handlePresent}
        currentPage="designer"
        onEndLesson={handleEndLesson}
      />
      {localTemplate && showSettingsModal && (
        <SettingsModalDesigner
          closeModal={handleShowSettingsModalCancel}
          openModal={showSettingsModal}
          templateId={templateId}
          localTemplate={localTemplate}
          localTemplateDispatch={localTemplateDispatch}
          isSavingTemplate={isSavingTemplate}
        />
      )}
      <BottomDrawer
        placement="bottom"
        headerStyle={{ display: 'none' }}
        closable={false}
        onClose={() => {
          closeDrawer();
        }}
        visible={showOptionsDrawer}
        mask={true}
        height="80%"
      >
        <BottomDrawerDragHandle onClose={() => closeDrawer()} />
        {selectedDrawerScreen === 'default' && (
          <>
            <h2 style={{ padding: '0px 20px' }}>
              <span>
                {(presentationTitle || t('Untitled presentation') || '').substring(0, 22)}
                {presentationTitle && presentationTitle?.length > 22 && '...'}
              </span>
            </h2>
            <DrawerHorisontalDivider />
            <DrawerMenuItemContainer>
              <div style={{ height: 24 }}>
                <SaveCloud
                  isSavingTemplate={isSavingTemplate}
                  isSavingTemplateSuccess={
                    isSavingTemplateSuccess || isSavingTemplateIdle
                  }
                  isSavingTemplateError={isSavingTemplateError}
                  color={theme.colors.black}
                />
              </div>
              <span>
                {isSavingTemplate
                  ? t('Saving')
                  : isSavingTemplateError
                  ? t('Saving failed')
                  : t('Save')}
              </span>
              <span
                style={{
                  fontSize: 14,
                  flex: 0,
                  whiteSpace: 'nowrap',
                  color: 'rgba(13,18,22,.7)',
                }}
              >
                {t('All changes saved')}
              </span>
            </DrawerMenuItemContainer>
            <DrawerMenuItemContainer
              onClick={() => {
                handleShowSettingsModal();
                closeDrawer();
              }}
              $disabled={!templateId}
            >
              <IconWrapper height="24px" width="24px" color={theme.colors.black}>
                <SettingsSvg />
              </IconWrapper>
              <span>{t('Settings')}</span>
            </DrawerMenuItemContainer>
            <DrawerHorisontalDivider />
            <DrawerMenuItemContainer
              onClick={() => localTemplate && handlePresentFromBeginning(localTemplate)}
            >
              <IconWrapper height="24px" width="24px" color={theme.colors.black}>
                <PlaySvg />
              </IconWrapper>
              <span>{t('Start now')}</span>
            </DrawerMenuItemContainer>
            <DrawerMenuItemContainer onClick={handleOpenPreview}>
              <IconWrapper height="24px" width="24px" color={theme.colors.black}>
                <FilledEyeSvg />
              </IconWrapper>
              <span>{t('Preview')}</span>
            </DrawerMenuItemContainer>
            {!!templateId && hasGames && (
              <Link href={`/${tenantId}/reports?templateId=${templateId}`}>
                <a>
                  <DrawerMenuItemContainer>
                    <IconWrapper height="24px" width="24px" color={theme.colors.black}>
                      <PollSvg />
                    </IconWrapper>
                    <span>{t('View report')}</span>
                  </DrawerMenuItemContainer>
                </a>
              </Link>
            )}
            <DrawerHorisontalDivider />
            <DrawerMenuItemContainer onClick={handleHelpClicked}>
              <IconWrapper height="24px" width="24px" color={theme.colors.black}>
                <QuestionMarkSvg />
              </IconWrapper>
              <span>{t('Help')}</span>
            </DrawerMenuItemContainer>
          </>
        )}

        {selectedDrawerScreen === 'share' && (
          <>
            <div
              style={{
                display: 'flex',
                height: 30,
                alignItems: 'center',
                padding: '0px 20px',
                cursor: 'pointer',
              }}
              onClick={() => setSelectedDrawerScreen('default')}
            >
              <IconWrapper height="30px" width="30px" color={theme.colors.black}>
                <ArrowLeftSvg />
              </IconWrapper>
              <span style={{ fontWeight: 'bold', fontSize: 22 }}>Back</span>
            </div>
            <DrawerHorisontalDivider />
            <CreatorShareComponent
              shouldShowDownloadAsPDF={shouldShowDownloadAsPDF}
              setShouldShowDownloadAsPDF={setShouldShowDownloadAsPDF}
              isGeneratedByAI={!!isGeneratedByAI}
              templateId={templateId}
              handleExportAsPdf={handleExportAsPdf}
              templateTitle={getTitle({
                title: localTemplate?.title,
                slides: localTemplate?.content.slides,
                maxLength: 30,
              })}
              isGenerator={!!localTemplate?.aiContent?.prompts?.length}
            />
          </>
        )}
      </BottomDrawer>
      <LeftActionGroupContainer>
        <Tooltip title={t('Exit the designer')} mouseEnterDelay={1}>
          <TopBarButton onClick={() => handleExit()}>
            <IconWrapper height="24px" width="24px" style={{ marginRight: -10 }}>
              <ArrowLeftSvg />
            </IconWrapper>
            <span>{t('Home')}</span>
          </TopBarButton>
        </Tooltip>
        {isLargerThan500 && (
          <Tooltip title={t('View settings')} mouseEnterDelay={1}>
            <TopBarButton
              onClick={handleShowSettingsModal}
              $disabled={!!!templateId}
              disabled={!!!templateId}
            >
              <span>{t('Settings')}</span>
            </TopBarButton>
          </Tooltip>
        )}

        {isLargerThan500 && <Divider />}
        <Tooltip title={`${t('Undo')} ${ctrlOrCMD}+Z`} mouseEnterDelay={1}>
          <TopBarButton
            $disabled={isUndoDisabled}
            disabled={isUndoDisabled}
            onClick={handleUndo}
          >
            <IconWrapper
              height="20px"
              width="20px"
              cursor={isUndoDisabled ? 'default' : undefined}
            >
              <UndoSvg />
            </IconWrapper>
          </TopBarButton>
        </Tooltip>
        <Tooltip title={`${t('Redo')} ⇧+${ctrlOrCMD}+Z`} mouseEnterDelay={1}>
          <TopBarButton
            $disabled={isRedoDisabled}
            disabled={isRedoDisabled}
            onClick={handleRedo}
          >
            <IconWrapper
              height="20px"
              width="20px"
              cursor={isRedoDisabled ? 'default' : undefined}
              style={{ transform: 'scaleX(-1)' }}
            >
              <UndoSvg />
            </IconWrapper>
          </TopBarButton>
        </Tooltip>
        {!isLargerThan500 && (
          <TopBarButton
            style={{ alignSelf: 'end' }}
            onClick={() => setShowOptionsDrawer(true)}
          >
            <IconWrapper
              height="24px"
              width="24px"
              style={{ transform: 'rotate(90deg)' }}
            >
              <MoreSvg />
            </IconWrapper>
          </TopBarButton>
        )}
        {isLargerThan600 && (
          <>
            <Divider />
            <span onClick={() => setShowVersionControl(true)}>
              <SaveCloud
                isSavingTemplate={isSavingTemplate}
                isSavingTemplateSuccess={isSavingTemplateSuccess || isSavingTemplateIdle}
                isSavingTemplateError={isSavingTemplateError}
              />
            </span>
          </>
        )}
      </LeftActionGroupContainer>
      <RightActionGroupContainer>
        {isLargerThan850 && (
          <TopBarButton
            onClick={handleShowSettingsModal}
            $disabled={!!!templateId}
            disabled={!!!templateId}
          >
            <PresentationTitleContainer>
              <span>
                {(presentationTitle || t('Untitled presentation') || '').substring(0, 21)}
                {presentationTitle && presentationTitle?.length > 20 && '...'}
              </span>
            </PresentationTitleContainer>
          </TopBarButton>
        )}
        {isLargerThan600 && !!templateId && hasGames && (
          <Link href={`/${tenantId}/reports?templateId=${templateId}`}>
            <a>
              <Tooltip title="View report" mouseEnterDelay={1}>
                <RightTopBarButton>
                  <IconWrapper height="20px" width="20px">
                    <PollSvg />
                  </IconWrapper>
                </RightTopBarButton>
              </Tooltip>
            </a>
          </Link>
        )}

        {isLargerThan500 && !!templateId && (
          <Tooltip
            title={
              isPresentingDisabled
                ? 'Add some slides to preview the lesson with a teacher and student view'
                : 'Preview the lesson with a teacher and student view'
            }
            mouseEnterDelay={1}
          >
            <div>
              <RightTopBarButton
                disabled={isPresentingDisabled}
                $disabled={isPresentingDisabled}
                onClick={handleOpenPreview}
              >
                <IconWrapper height="20px" width="20px">
                  {isLoadingPreview ? <LoadingOutlined /> : <FilledEyeSvg />}
                </IconWrapper>
                {isLargerThan700 && <span>Preview</span>}
              </RightTopBarButton>
            </div>
          </Tooltip>
        )}
        <Tooltip
          title={
            isPresentingDisabled
              ? 'Add some slides to start the lesson with your students'
              : 'Start the lesson now with your students'
          }
          mouseEnterDelay={1}
        >
          <div>
            <Button
              onClick={() => localTemplate && handlePresentFromBeginning(localTemplate)}
              disabled={isPresentingDisabled}
              loading={isCreatingGame}
              type="white"
              icon={<PlaySvg />}
              styles={{
                height: 43,
              }}
            >
              {isLargerThan500 ? t('Start now') : ' '}
            </Button>
          </div>
        </Tooltip>
      </RightActionGroupContainer>
      {showVersionControl && (
        <VersionControl
          presentationId={templateId}
          handleClose={() => setShowVersionControl(false)}
        />
      )}
    </TopbarContainer>
  );
};

export default DesignerTopBar;

const DrawerHorisontalDivider = () => {
  return (
    <DividerHorisontalContainer>
      <DividerHorisontalContent />
    </DividerHorisontalContainer>
  );
};

const DividerHorisontalContainer = styled.div`
  height: 1px;
  width: 100%;
  margin-top: 6px;
  margin-bottom: 6px;
`;
const DividerHorisontalContent = styled.div`
  height: 100%;
  width: 100%;
  background: ${theme.colors.grey};
  border-radius: 5px;
`;

const Divider = () => {
  return (
    <DividerContainer>
      <DividerContent />
    </DividerContainer>
  );
};

const DividerContainer = styled.div`
  width: 1px;
  height: 100%;
  padding: 8px 0px;
`;
const DividerContent = styled.div`
  height: 100%;
  width: 100%;
  background: ${theme.colors.moreTranslucentWhite};
  border-radius: 5px;
`;

const PresentationTitleContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: bold;
    color: ${theme.colors.white};
    font-size: 14px;
  }
`;

const TopBarButton = styled.button<{ $disabled?: boolean }>`
  height: 100%;
  padding: 0px 14px;

  background: transparent;
  outline: none;
  border: none;
  cursor: pointer;
  border-radius: 5px;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: ${theme.colors.moreTranslucentWhite};
  }

  &:active {
    background: ${theme.colors.translucentWhite};
  }

  // text
  font-size: 16px;
  font-weight: bold;
  color: ${theme.colors.white};
  line-height: 22px;

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      color: ${theme.colors.moreTranslucentWhite};
      &:hover {
        background: transparent;
      }
      &:active {
        background: transparent;
      }
    `}
`;

const RightTopBarButton = styled(TopBarButton)`
  color: ${theme.colors.white};
  background: rgba(255, 255, 255, 0.2);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  &:active {
    background: rgba(255, 255, 255, 0.4);
  }
`;

const ActionGroupContainer = styled.div`
  display: flex;
  height: 100%;
  gap: 8px;
`;

const LeftActionGroupContainer = styled(ActionGroupContainer)`
  flex: 1;
`;
const RightActionGroupContainer = styled(ActionGroupContainer)`
  flex: 0;
`;

const TopbarContainer = styled(DesignerTopBarContainer)`
  position: relative;
  display: flex;
  justify-content: flex-start;
  padding: 8px;
`;

const BottomDrawer = styled(Drawer)`
  z-index: ${zIndexes('ANTD_TOPBAR_DRAWER')} !important;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1) !important;

  .ant-drawer-content {
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
  }
  .ant-drawer-body {
    scrollbar-width: none;
    padding: 20px 0px;
    max-height: 80vh;
    overflow-y: auto;
  }

  .ant-drawer-content-wrapper {
    border-radius: inherit;
  }
`;

const DrawerMenuItemContainer = styled.div<{ $disabled?: boolean }>`
  width: 100%;
  height: 42px;
  display: flex;
  align-items: center;
  padding: 0px 20px;
  gap: 10px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};
  span {
    font-size: 16px;
    flex: 1;
    user-select: none;
  }

  &:hover {
    background: #d4d6db;
  }
  &:active {
    background: #c4c4c6;
  }
`;
